import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, TableSortLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { NoContent, ReactTable } from '../../../components/shared';
import { routeConstants } from '../../../constants/routeConstants';
import { useMonthlyReconciliation } from '../../../hooks';
import { DEFAULT_PER_PAGE, DEFAULT_PAGE } from '../../../constants/pagination';
import useStyles from './style';

const TABLE_PAGE_INDEX_OFFSET = 1;

const MonthlyReconciliationTable = ({
  data,
  searchText,
  filterParams,
  config,
  columns,
  sortComparators,
  detailRoute,
  noDataTitle,
  noDataDescription,
  initialSortBy = null,
  sortBy: controlledSortBy,
  onSortChange: onSortChangeFromParent,
  totalCount,
  pageNumber,
  rowsPerPage,
  onPageChange,
  onPerPageChange,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [localSortBy, setLocalSortBy] = useState(initialSortBy);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const serverSidePagination =
    totalCount !== undefined && typeof onPageChange === 'function' && typeof onPerPageChange === 'function';

  const sortBy = onSortChangeFromParent && controlledSortBy != null ? controlledSortBy : localSortBy;

  const handleSortChange = useCallback(
    (columnId) => {
      const isActive = sortBy?.id === columnId;
      const isDesc = isActive ? !sortBy?.desc : false;
      if (onSortChangeFromParent) {
        onSortChangeFromParent(columnId, isDesc);
      } else {
        setLocalSortBy({ id: columnId, desc: isDesc });
      }
    },
    [sortBy, onSortChangeFromParent],
  );

  const columnsWithSorting = useMemo(
    () =>
      columns.map((column) => {
        if (!column.canSort) return column;

        const isActive = sortBy?.id === column.id;
        const isDesc = isActive ? !!sortBy?.desc : false;
        const originalHeader = column.Header;

        return {
          ...column,
          Header: (headerProps) => (
            <TableSortLabel
              active={isActive}
              direction={isDesc ? 'desc' : 'asc'}
              onClick={() => handleSortChange(column.id)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {typeof originalHeader === 'function' ? originalHeader(headerProps) : originalHeader}
            </TableSortLabel>
          ),
        };
      }),
    [columns, handleSortChange, sortBy],
  );

  const sortedData = useMonthlyReconciliation({
    data,
    searchText,
    filterParams,
    sortBy,
    sortComparators,
    config,
    disabled: serverSidePagination,
  });

  const dataForTable = serverSidePagination ? data : sortedData;

  const dataWithClickHandler = useMemo(
    () =>
      (dataForTable || []).map((row) => ({
        ...row,
        onClick:
          row?.id && detailRoute
            ? () => navigate(`/${routeConstants.MONTHLY_RECONCILIATION_ROUTE}/${detailRoute}/${row.id}`)
            : undefined,
      })),
    [detailRoute, navigate, dataForTable],
  );

  const tableData = useMemo(() => {
    if (serverSidePagination) return dataWithClickHandler;
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return dataWithClickHandler.slice(startIndex, endIndex);
  }, [dataWithClickHandler, currentPage, perPage, serverSidePagination]);

  const handlePageChangeLocal = useCallback((page) => setCurrentPage(page), []);
  const handlePerPageChangeLocal = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(DEFAULT_PAGE);
  }, []);

  useEffect(() => {
    if (!serverSidePagination) setCurrentPage(DEFAULT_PAGE);
  }, [searchText, filterParams, sortBy, serverSidePagination]);

  const showNoContent = serverSidePagination ? (totalCount ?? 0) === 0 : !dataWithClickHandler?.length;

  if (showNoContent) {
    return (
      <NoContent
        title={noDataTitle || 'No data found'}
        desc={<>{noDataDescription || 'Try adjusting your filters'}</>}
      />
    );
  }

  return (
    <Box className={classes.monthlyReconciliationTable}>
      <ReactTable
        columns={columnsWithSorting}
        data={tableData}
        enableRowClick
        rowClassName={() => classes.clickableRow}
        pagination
        manualPagination
        totalCount={serverSidePagination ? totalCount : dataWithClickHandler.length}
        pageNumberForTablePagination={
          serverSidePagination
            ? (pageNumber ?? DEFAULT_PAGE) - TABLE_PAGE_INDEX_OFFSET
            : currentPage - TABLE_PAGE_INDEX_OFFSET
        }
        onPageChange={serverSidePagination ? onPageChange : handlePageChangeLocal}
        onPerPageChange={serverSidePagination ? onPerPageChange : handlePerPageChangeLocal}
        perPage={serverSidePagination ? rowsPerPage ?? DEFAULT_PER_PAGE : perPage}
      />
    </Box>
  );
};

export default MonthlyReconciliationTable;
